import React from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// Renders Thriver's replies with light markdown support (bold, italic, lists,
// links). Anchors are governed by an explicit allow-list of exact permitted
// paths passed in as `allowedPaths` — the same catalogue the backend embeds in
// Thriver's system prompt — so the model cannot invent internal URLs.
// External links render only when they use http(s). Every other href renders
// as plain text (no <a>).
export const ChatMarkdown = ({ children, allowedPaths = [] }) => {
  const permitted = React.useMemo(() => new Set(allowedPaths), [allowedPaths]);
  return (
    <div className="font-serif text-sm sm:text-base leading-relaxed">
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
          strong: ({ node, ...props }) => (
            <strong className="font-semibold text-primary-custom" {...props} />
          ),
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          ul: ({ node, ...props }) => (
            <ul className="list-disc pl-5 mb-3 last:mb-0 space-y-1.5" {...props} />
          ),
          ol: ({ node, ...props }) => (
            <ol className="list-decimal pl-5 mb-3 last:mb-0 space-y-1.5" {...props} />
          ),
          li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
          h1: ({ node, ...props }) => <p className="font-semibold mb-2" {...props} />,
          h2: ({ node, ...props }) => <p className="font-semibold mb-2" {...props} />,
          h3: ({ node, ...props }) => <p className="font-semibold mb-2" {...props} />,
          a: ({ node, href = "", children, ...props }) => {
            // Internal — must be an EXACT match against the permitted set. Same tab, SPA nav.
            if (href.startsWith("/")) {
              if (permitted.has(href)) {
                return (
                  <Link
                    to={href}
                    data-testid="thriver-internal-link"
                    className="inline-flex items-baseline gap-1 underline decoration-[var(--accent-bronze)] underline-offset-2 hover:text-[var(--accent-bronze)] transition-colors"
                  >
                    {children}
                    <ArrowRight className="w-3 h-3 shrink-0 self-center" aria-hidden="true" />
                  </Link>
                );
              }
              return <span data-testid="thriver-rejected-link">{children}</span>;
            }
            // External — only http(s). Anything else (mailto:, javascript:, tel:, no protocol)
            // is rendered as plain text.
            if (/^https?:\/\//i.test(href)) {
              return (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="thriver-external-link"
                  className="underline decoration-[var(--accent-bronze)] underline-offset-2 hover:text-[var(--accent-bronze)] transition-colors"
                  {...props}
                >
                  {children}
                </a>
              );
            }
            return <span data-testid="thriver-rejected-link">{children}</span>;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};
